import {
  Box,
  Typography,
  Collapse,
  useTheme,
  useMediaQuery,
  IconButton,
  Slider,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import theme from "../theme";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

// Icons
import OutlinedFlagRoundedIcon from "@mui/icons-material/OutlinedFlagRounded";
import HistoryToggleOffRoundedIcon from "@mui/icons-material/HistoryToggleOffRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VideoFileRoundedIcon from "@mui/icons-material/VideoFileRounded";
import TextSnippetRoundedIcon from "@mui/icons-material/TextSnippetRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";

function TicketActivityTimelineCard({ ticketTimeline, ticket }) {
  const ticketLogsArr = ticketTimeline.slice().reverse() || [];
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const [expandCreateInfoCard, setExpandCreateInfoCard] = useState(true);
  const [expandedCardId, setExpandedCardId] = useState(ticketTimeline[0]?.id);
  const [ticketLogOverlayFile, setTicketLogOverlayFile] = useState(null);
  const [ticketOverlayFile, setTicketOverlayFile] = useState(null);

  const priorityMapping = {
    low: "Low",
    medium: "Medium",
    high: "High",
  };

  // helper to detect the single audio file
  const isAudioUrl = (url) => {
    const ext = url.split(".").pop().toLowerCase().split("?")[0];
    return ["mp3", "wav", "ogg", "m4a", "webm"].includes(ext);
  };

  // Pick the only audio file
  const ticketAudioURL = ticket?.media?.find(isAudioUrl) || "";

  // Safely parse media (string or array → always array)
  const parseMediaArray = (media) => {
    try {
      return Array.isArray(media) ? media : JSON.parse(media || "[]");
    } catch (err) {
      console.error("Failed to parse media array", err);
      return [];
    }
  };

  // Audio fetcher
  const logAudioFetcher = (log) => {
    const mediaArr = parseMediaArray(log?.media);
    return mediaArr.find((url) => isAudioUrl(url)) || "";
  };

  // Custom audio player
  function AudioPlayer({ url }) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const updateProgress = () => setProgress(audio.currentTime);
      const setMeta = () => setDuration(audio.duration);

      audio.addEventListener("timeupdate", updateProgress);
      audio.addEventListener("loadedmetadata", setMeta);

      return () => {
        audio.removeEventListener("timeupdate", updateProgress);
        audio.removeEventListener("loadedmetadata", setMeta);
      };
    }, []);

    const togglePlay = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (isPlaying) audio.pause();
      else audio.play();
      setIsPlaying(!isPlaying);
    };

    const handleSliderChange = (_, value) => {
      audioRef.current.currentTime = value;
      setProgress(value);
    };

    const toggleMute = () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.muted = !isMuted;
      setIsMuted(!isMuted);
    };

    if (!url) return null;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          bgcolor: "#E1EFFF",
          borderRadius: 2,
          pr: 0.5,
        }}
      >
        <audio ref={audioRef} src={url} style={{ display: "none" }} />
        <IconButton onClick={togglePlay}>
          {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
        </IconButton>
        <Slider
          min={0}
          max={duration}
          value={progress}
          onChange={handleSliderChange}
          sx={{
            width: "50%",
            flexGrow: 1,
            mx: 0.5,
            color: theme.lightTheme.themeCol,
            "& .MuiSlider-thumb": {
              backgroundColor: theme.lightTheme.themeCol,
            },
          }}
        />
        <IconButton onClick={toggleMute}>
          {isMuted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
        </IconButton>
        <Typography variant="caption">
          {Math.floor(progress)}s/{Math.floor(duration)}s
        </Typography>
      </Box>
    );
  }

  // Extract extension and filename from URL
  const getFileMeta = (url) => {
    const filename = url.split("/").pop();
    const ext = filename.split(".").pop().toLowerCase();

    let type = "other";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) type = "image";
    else if (["mp3", "wav", "ogg", "m4a"].includes(ext)) type = "audio";
    else if (ext === "webm") type = "audio"; // ✅ treat recorded webm as audio
    else if (["mp4", "mkv"].includes(ext)) type = "video";
    else if (ext === "pdf") type = "pdf";
    else if (["txt", "csv", "doc", "docx"].includes(ext)) type = "text";

    return { filename, type, ext };
  };

  // Date formatter
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();

    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    if (isToday) {
      return `${hours}:${minutes}`; // only show time
    }

    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];

    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";

    return `${day}${suffix} ${month}, ${hours}:${minutes}`;
  };

  // Days gap calculator
  const getDayGapText = (dateString) => {
    const givenDate = new Date(dateString);
    const today = new Date();

    // Normalize time to midnight for accurate day calculation
    givenDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const timeDiff = today - givenDate;
    const dayGap = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (dayGap === 0) {
      return `Today : ${formatDate(dateString)}`;
    } else if (dayGap === 1) {
      return `1 day ago : ${formatDate(dateString)}`;
    } else {
      return `${dayGap} days ago : ${formatDate(dateString)}`;
    }
  };

  // Time budget calculator
  const totalTimeBudget = (givenArray) => {
    let timeBudgetSum;
    timeBudgetSum = givenArray.reduce((acc, el) => (el.time_budget += acc), 0);
    return timeBudgetSum;
  };

  // Time taken calculator
  const totalTimeTaken = (givenArray) => {
    let timeTakenSum;
    timeTakenSum = givenArray.reduce((acc, el) => (el.time_taken += acc), 0);
    return timeTakenSum;
  };

  // Media file downloader
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Network response not ok");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download failed:", err);
      // Fallback: open in new tab so the user can save manually
      window.open(url, "_blank", "noopener");
    }
  };

  return (
    <>
      {/* Show Created Info (if ticket is open) */}
      {ticketTimeline.length === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexDirection: "column",
            gap: 0.5,
            width: "100%",

            px: 1,
            py: 1,
            borderRadius: 2,
            border: `1px solid #D4D4D4`,
          }}
        >
          {/* Ticket Created Message */}
          <Typography sx={{ fontSize: 14, color: "black" }}>
            {ticket.created_by_name !== null
              ? `${ticket.created_by_name} `
              : "-- "}
            created a ticket
            {ticket.status === "open"
              ? ""
              : ` and assigned to ${ticketTimeline[0]?.assigned_to_name}`}
          </Typography>

          {/* Description */}
          <Typography sx={{ fontSize: 14, color: "#5A5A5A" }}>
            {ticket.description}
          </Typography>

          {/* Priority & Created Date Container */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* Priority */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <OutlinedFlagRoundedIcon
                sx={{
                  fill: theme.lightTheme.priorities[ticket.priority].textCol,
                }}
              />
              <Typography sx={{ fontSize: 12 }}>
                {priorityMapping[ticket.priority]}
              </Typography>
            </Box>

            {/* Created Since */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <HistoryToggleOffRoundedIcon sx={{ fill: "#FF474A" }} />
              <Typography sx={{ fontSize: 12 }}>
                {getDayGapText(ticket.created_at)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Show All Logs (if ticket is not open) */}
      {ticketTimeline.length !== 0 && (
        <>
          {/* The Latest Ticket Info (always show) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",

              px: 1,
              py: 1,
              borderRadius: 2,
              border: `1px solid ${
                theme.lightTheme.statuses[ticketLogsArr[0].status].borderCol
              }`,
              bgcolor: theme.lightTheme.statuses[ticketLogsArr[0].status].bgCol,
              mb: 3,
            }}
          >
            {/* Info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                gap: 0.5,
                width: "100%",
              }}
            >
              {/* Ticket Status Message */}
              <Typography sx={{ fontSize: 14, color: "black" }}>
                {/* resolved */}
                {ticketLogsArr[0].status === "resolved" &&
                  `${ticketLogsArr[0].assigned_to_name} resolved the ticket`}

                {/* rejected */}
                {ticketLogsArr[0].status === "rejected" &&
                  `${ticketLogsArr[0].assigned_to_name} rejected the ticket`}

                {/* pending */}
                {ticketLogsArr[0].status === "in_progress" &&
                  `${ticketLogsArr[0].assigned_to_name} changed status to pending`}
              </Typography>

              {/* Updated Date & Time */}
              <Typography sx={{ fontSize: 14, color: "#5A5A5A" }}>
                {formatDate(ticketLogsArr[0].updated_at)}
              </Typography>

              {/* Time Taken & Budget Container */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  width: "100%",
                  gap: 0.5,
                }}
              >
                {/* Time Taken */}
                <Typography sx={{ fontSize: 12 }}>
                  Time Taken :
                  <span
                    style={{
                      color:
                        theme.lightTheme.statuses[ticketLogsArr[0].status]
                          .textCol,
                    }}
                  >
                    {" "}
                    {ticketLogsArr[0].time_taken} min
                  </span>
                </Typography>

                {/* Separator Line */}
                <Box
                  sx={{
                    width: "1px",
                    height: "14px",
                    bgcolor: "black",
                  }}
                />

                {/* Time Budget */}
                <Typography sx={{ fontSize: 12 }}>
                  Budget :
                  <span
                    style={{
                      color:
                        theme.lightTheme.statuses[ticketLogsArr[0].status]
                          .textCol,
                    }}
                  >
                    {" "}
                    {ticketLogsArr[0].time_budget} min
                  </span>
                </Typography>
              </Box>
            </Box>

            {/* Icon */}
            {(ticketLogsArr[0].status === "in_progress" ||
              ticketLogsArr[0].status === "follow_up" ||
              ticketLogsArr[0].status === "recurring") && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 45,
                  minHeight: 45,
                  borderRadius: 100,
                  bgcolor: "#FFECC4",
                  border: "4px solid #FFF2D6",
                  boxShadow: "inset 0px -2px 4px #F7B733",
                }}
              >
                <HistoryRoundedIcon sx={{ fontSize: 18, fill: "#FFAC00" }} />
              </Box>
            )}

            {ticketLogsArr[0].status === "resolved" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 45,
                  minHeight: 45,
                  borderRadius: "50%",
                  bgcolor: "#24B567",
                  border: "4px solid #C4FFDF",
                  boxShadower: "inset 0px -1px 4px #00000040",
                }}
              >
                <DoneRoundedIcon sx={{ fontSize: 18, fill: "white" }} />
              </Box>
            )}

            {ticketLogsArr[0].status === "rejected" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 45,
                  minHeight: 45,
                  borderRadius: "50%",
                  bgcolor: "#FF5D60",
                  border: "4px solid #FFDEDE",
                  boxShadower: "inset 0px -1px 4px #00000040",
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18, fill: "white" }} />
              </Box>
            )}
          </Box>

          {/* Dynamic Indicator Bar & Ticket Logs Section */}
          <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
            {/* Dynamic Indicator Bar */}
            <Box
              sx={{
                position: "relative",
                width: "24px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {/* Vertical Line */}
              <Box
                sx={{
                  position: "absolute",
                  top: "16px", // Start after some top margin
                  bottom: "60px", // End before last card
                  width: "1px",
                  bgcolor: "#D1D1D1",
                }}
              />
            </Box>

            {/* All Log Cards */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
            >
              {/* ---------------------------------------------------- */}
              {/* Log Cards */}
              {ticketLogsArr?.map((ticketLog) => (
                <Box
                  key={ticketLog.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    gap: 0.5,
                    width: "100%",

                    px: 1,
                    py: 1,
                    borderRadius: 2,
                    border: `1px solid #D4D4D4`,

                    position: "relative",
                  }}
                >
                  {/* Indicator Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      borderRadius: 100,
                      bgcolor: "#FFF3D9",
                      border: "0.5px solid #F7B733",
                      bgcolor:
                        ticketLog.status === "resolved" ? "#24B567" : "#FFF3D9",
                      border:
                        ticketLog.status === "resolved"
                          ? "0.5px solid #C4FFDF"
                          : "0.5px solid #F7B733",

                      position: "absolute",
                      left: -46,
                      top: 0,
                    }}
                  >
                    {ticketLog.status === "resolved" ? (
                      <DoneRoundedIcon sx={{ fontSize: 24, fill: "#FFF" }} />
                    ) : (
                      <GroupAddRoundedIcon
                        sx={{ fontSize: 24, fill: "#8E6000" }}
                      />
                    )}
                  </Box>

                  {/* Heading Message & Expand Icon Container */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    {/* Heading */}
                    <Typography sx={{ fontSize: 14, color: "black" }}>
                      {ticketLog.assigner_name !== null
                        ? `${ticketLog.assigner_name} `
                        : "-- "}
                      {ticketLog.status === "open"
                        ? "created a ticket"
                        : ` assigned to ${
                            ticketLog?.assigned_from === ticketLog?.assigned_to
                              ? "self"
                              : ticketLog?.assigned_to_name
                          }`}
                    </Typography>

                    {/* Expand Icon */}
                    {ticketLog?.media?.length > 0 && (
                      <ExpandMoreIcon
                        sx={{
                          justifySelf: "flex-end",
                          fontSize: 24,
                          color: theme.lightTheme.placeholder,
                          transform:
                            expandedCardId === ticketLog.id
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setExpandedCardId((prevId) =>
                            prevId === ticketLog.id ? null : ticketLog.id
                          )
                        }
                      />
                    )}
                  </Box>

                  {/* Description */}
                  <Typography sx={{ fontSize: 14, color: "#5A5A5A" }}>
                    {ticketLog.description}
                  </Typography>

                  {/* Media Files (conditionally shown) */}
                  <Collapse
                    in={expandedCardId === ticketLog.id}
                    sx={{ width: "100%" }}
                  >
                    {parseMediaArray(ticketLog.media).filter(
                      (url) => !isAudioUrl(url)
                    ).length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1.5,
                          mt: 2,
                          mb: 1,
                        }}
                      >
                        {parseMediaArray(ticketLog.media)
                          .filter((url) => !isAudioUrl(url)) // skip audio, render only image/video/pdf
                          .map((url, index) => {
                            const meta = getFileMeta(url);
                            return (
                              <Box
                                key={index}
                                sx={{
                                  position: "relative",
                                  width: 96,
                                  height: 96,
                                  borderRadius: 1.5,
                                  backgroundImage:
                                    meta.type === "image"
                                      ? `url(${url})`
                                      : "none",
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  setTicketLogOverlayFile({ url, ...meta })
                                }
                              >
                                {meta.type === "pdf" && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexDirection: "column",
                                      width: "100%",
                                      height: "100%",
                                      border: `1px solid ${theme.lightTheme.themeCol}`,
                                      borderRadius: 3,
                                    }}
                                  >
                                    <TextSnippetRoundedIcon
                                      sx={{
                                        width: 56,
                                        height: 64,
                                        fill: theme.lightTheme.themeCol,
                                      }}
                                    />
                                  </Box>
                                )}

                                {meta.type === "video" && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexDirection: "column",
                                      width: "100%",
                                      height: "100%",
                                      border: `1px solid ${theme.lightTheme.themeCol}`,
                                      borderRadius: 3,
                                    }}
                                  >
                                    <VideoFileRoundedIcon
                                      sx={{
                                        width: 56,
                                        height: 64,
                                        fill: theme.lightTheme.themeCol,
                                      }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                      </Box>
                    )}

                    {/* Audio Player */}
                    {logAudioFetcher(ticketLog) && (
                      <AudioPlayer url={logAudioFetcher(ticketLog)} />
                    )}
                  </Collapse>

                  {/* Fullscreen Media View */}
                  {ticketLogOverlayFile && (
                    <Box
                      sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.8)",
                        backdropFilter: "blur(8px)",
                        WebkitBackdropFilter: "blur(8px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: isMobile ? 999 : 99999,
                      }}
                      onClick={() => setTicketLogOverlayFile(null)}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          maxWidth: "95vw",
                          maxHeight: "95vh",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* IMAGE */}
                        {ticketLogOverlayFile?.type === "image" && (
                          <TransformWrapper
                            initialScale={1}
                            minScale={1}
                            wheel={{ step: 0.1 }}
                            pinch={{ step: 5 }}
                            doubleClick={{ disabled: false, step: 1.5 }}
                          >
                            <TransformComponent>
                              <img
                                src={ticketLogOverlayFile.url}
                                alt={ticketLogOverlayFile.filename}
                                style={{
                                  maxWidth: "95vw",
                                  maxHeight: "85vh", // leave space for button
                                  display: "block",
                                }}
                              />
                            </TransformComponent>
                          </TransformWrapper>
                        )}

                        {/* VIDEO */}
                        {ticketLogOverlayFile?.type === "video" && (
                          <video
                            controls
                            style={{
                              maxWidth: "95vw",
                              maxHeight: "85vh",
                              display: "block",
                            }}
                          >
                            <source
                              src={ticketLogOverlayFile.url}
                              type={`video/${ticketLogOverlayFile.ext}`}
                            />
                          </video>
                        )}

                        {/* PDF */}
                        {ticketLogOverlayFile?.type === "pdf" && (
                          <object
                            data={ticketLogOverlayFile.url}
                            type="application/pdf"
                            style={{
                              width: isMobile ? "100%" : "95vw",
                              height: isMobile ? "100%" : "85vh",
                              backgroundColor: isMobile ? "#FFF" : "none",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "column",
                                width: "100%",
                              }}
                            >
                              <Typography>
                                PDF preview not supported.
                              </Typography>
                              <Typography
                                onClick={async () =>
                                  downloadFile(
                                    ticketLogOverlayFile.url,
                                    ticketLogOverlayFile.filename
                                  )
                                }
                              >
                                Click{" "}
                                <span
                                  style={{
                                    color: "blue",
                                    textDecoration: "underline",
                                  }}
                                >
                                  here
                                </span>{" "}
                                to download the PDF
                              </Typography>
                            </Box>
                          </object>
                        )}

                        {/* Download Button */}
                        {ticketLogOverlayFile?.type !== "pdf" && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              bgcolor: "#FFF",
                              position: "absolute", // ✅ anchor inside parent
                              top: 8,
                              right: 8,
                              boxShadow: 2,
                              cursor: "pointer",
                            }}
                            onClick={async () =>
                              downloadFile(
                                ticketLogOverlayFile.url,
                                ticketLogOverlayFile.filename
                              )
                            }
                          >
                            <FileDownloadRoundedIcon
                              sx={{
                                fontSize: 24,
                                fill: theme.lightTheme.themeCol,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Assigned To, Priority & Created Date Container */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    {/* Assigned To (show only if assigned to someone) */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <PersonAddAltRoundedIcon sx={{ fill: "#008080" }} />
                      <Typography sx={{ fontSize: 12 }}>
                        {ticketLog.assigned_to_name}
                      </Typography>
                    </Box>

                    {/* Priority */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <OutlinedFlagRoundedIcon
                        sx={{
                          fill: theme.lightTheme.priorities[ticket.priority]
                            .textCol,
                        }}
                      />
                      <Typography sx={{ fontSize: 12 }}>
                        {priorityMapping[ticket.priority]}
                      </Typography>
                    </Box>

                    {/* Created Since */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <HistoryToggleOffRoundedIcon sx={{ fill: "#FF474A" }} />
                      <Typography sx={{ fontSize: 12 }}>
                        {getDayGapText(ticket.updated_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}

              {/* ---------------------------------------------------- */}
              {/* Ticket Creation Info */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: 0.5,
                  width: "100%",

                  px: 1,
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid #D4D4D4`,

                  position: "relative",
                }}
              >
                {/* Indicator Icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    borderRadius: 100,
                    bgcolor: "#E0ECFF",
                    border: "0.5px solid #0E69FF",

                    position: "absolute",
                    left: -46,
                    top: 0,
                  }}
                >
                  <ConfirmationNumberRoundedIcon
                    sx={{ fontSize: 18, fill: "#0E69FF" }}
                  />
                </Box>

                {/* Ticket Created Message & Expand Icon (conditionally shown) */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {/* Heading */}
                  <Typography sx={{ fontSize: 14, color: "black" }}>
                    {ticket.created_by_name !== null
                      ? `${ticket.created_by_name} `
                      : "-- "}
                    created a ticket
                  </Typography>

                  {/* Expand Icon */}
                  {ticket?.media?.length > 0 && (
                    <ExpandMoreIcon
                      sx={{
                        justifySelf: "flex-end",
                        fontSize: 24,
                        color: theme.lightTheme.placeholder,
                        transform: expandCreateInfoCard
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setExpandCreateInfoCard((prevState) => !prevState)
                      }
                    />
                  )}
                </Box>

                {/* Description */}
                <Typography sx={{ fontSize: 14, color: "#5A5A5A" }}>
                  {ticket.description}
                </Typography>

                {/* Media Files (conditionally shown) */}
                <Collapse in={expandCreateInfoCard} sx={{ width: "100%" }}>
                  {ticket?.media?.filter((url) => !isAudioUrl(url))?.length >
                    0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                        mt: 1,
                        mb: 1,
                      }}
                    >
                      {(() => {
                        // Defensive parsing
                        let mediaArray = [];
                        try {
                          mediaArray = Array.isArray(ticket.media)
                            ? ticket.media
                            : JSON.parse(ticket.media || "[]");
                        } catch (err) {
                          console.error("Failed to parse media", err);
                        }

                        return mediaArray
                          .filter((url) => !isAudioUrl(url))
                          .map((url, index) => {
                            const meta = getFileMeta(url);
                            return (
                              <Box
                                key={index}
                                sx={{
                                  position: "relative",
                                  width: 96,
                                  height: 96,
                                  borderRadius: 1.5,
                                  backgroundImage:
                                    meta.type === "image"
                                      ? `url(${url})`
                                      : "none",
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  setTicketOverlayFile({ url, ...meta })
                                }
                              >
                                {meta.type === "text" || meta.type === "pdf" ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexDirection: "column",
                                      width: "100%",
                                      height: "100%",
                                      border: `1px solid ${theme.lightTheme.themeCol}`,
                                      borderRadius: 3,
                                    }}
                                  >
                                    <TextSnippetRoundedIcon
                                      sx={{
                                        width: 56,
                                        height: 64,
                                        fill: theme.lightTheme.themeCol,
                                      }}
                                    />
                                  </Box>
                                ) : null}

                                {meta.type === "video" && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexDirection: "column",
                                      width: "100%",
                                      height: "100%",
                                      border: `1px solid ${theme.lightTheme.themeCol}`,
                                      borderRadius: 3,
                                    }}
                                  >
                                    <VideoFileRoundedIcon
                                      sx={{
                                        width: 56,
                                        height: 64,
                                        fill: theme.lightTheme.themeCol,
                                      }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            );
                          });
                      })()}
                    </Box>
                  )}

                  {/* Audio Player */}
                  {ticketAudioURL && <AudioPlayer url={ticketAudioURL} />}
                </Collapse>

                {/* Fullscreen Media View */}
                {ticketOverlayFile && (
                  <Box
                    sx={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      backgroundColor: "rgba(0,0,0,0.8)",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: isMobile ? 999 : 99999,
                    }}
                    onClick={() => setTicketOverlayFile(null)}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* IMAGE */}
                      {ticketOverlayFile?.type === "image" && (
                        <TransformWrapper
                          initialScale={1}
                          minScale={1}
                          wheel={{ step: 0.1 }}
                          pinch={{ step: 5 }}
                          doubleClick={{ disabled: false, step: 1.5 }}
                        >
                          <TransformComponent>
                            <img
                              src={ticketOverlayFile.url}
                              alt={ticketOverlayFile.filename}
                              style={{
                                maxWidth: "95vw",
                                maxHeight: "85vh", // leave space for button
                                display: "block",
                              }}
                            />
                          </TransformComponent>
                        </TransformWrapper>
                      )}

                      {/* VIDEO */}
                      {ticketOverlayFile?.type === "video" && (
                        <video
                          controls
                          style={{
                            maxWidth: "95vw",
                            maxHeight: "85vh",
                            display: "block",
                          }}
                        >
                          <source
                            src={ticketOverlayFile.url}
                            type={`video/${ticketOverlayFile.ext}`}
                          />
                        </video>
                      )}

                      {/* PDF */}
                      {ticketOverlayFile?.type === "pdf" && (
                        <object
                          data={ticketOverlayFile.url}
                          type="application/pdf"
                          style={{
                            width: isMobile ? "100%" : "95vw",
                            height: isMobile ? "100%" : "85vh",
                            backgroundColor: isMobile ? "#FFF" : "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexDirection: "column",
                              width: "100%",
                            }}
                            onClick={async () =>
                              downloadFile(
                                ticketOverlayFile.url,
                                ticketOverlayFile.filename
                              )
                            }
                          >
                            <Typography>PDF preview not supported.</Typography>
                            <Typography>
                              Click{" "}
                              <span
                                style={{
                                  color: "blue",
                                  textDecoration: "underline",
                                }}
                              >
                                here
                              </span>{" "}
                              to download the PDF
                            </Typography>
                          </Box>
                        </object>
                      )}

                      {/* Download Button */}
                      {ticketOverlayFile?.type !== "pdf" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "#FFF",
                            position: "absolute", // ✅ anchor inside parent
                            top: 8,
                            right: 8,
                            boxShadow: 2,
                            cursor: "pointer",
                          }}
                          onClick={async () =>
                            downloadFile(
                              ticketOverlayFile.url,
                              ticketOverlayFile.filename
                            )
                          }
                        >
                          <FileDownloadRoundedIcon
                            sx={{
                              fontSize: 24,
                              fill: theme.lightTheme.themeCol,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Priority & Created Date Container */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {/* Priority */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <OutlinedFlagRoundedIcon
                      sx={{
                        fill: theme.lightTheme.priorities[ticket.priority]
                          .textCol,
                      }}
                    />
                    <Typography sx={{ fontSize: 12 }}>
                      {priorityMapping[ticket.priority]}
                    </Typography>
                  </Box>

                  {/* Created Since */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <HistoryToggleOffRoundedIcon sx={{ fill: "#FF474A" }} />
                    <Typography sx={{ fontSize: 12 }}>
                      {getDayGapText(ticket.created_at)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* ---------------------------------------------------- */}
              {/* Total Activity Summary */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",

                  px: 1,
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid #D4D4D4`,
                  minHeight: "65px",

                  position: "relative",
                }}
              >
                {/* Indicator Icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    borderRadius: 100,
                    bgcolor: "#FFF7F3",
                    border: "0.5px solid #FF474A",

                    position: "absolute",
                    left: -46,
                    top: 0,
                  }}
                >
                  <HistoryRoundedIcon sx={{ fontSize: 24, fill: "#F14F4A" }} />
                </Box>

                {/* Heading */}
                <Typography sx={{ fontSize: 14, color: "#FF474A" }}>
                  Total Activity Summary
                </Typography>

                {/* Time Spent & Status Changes Container */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {/* Priority */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Typography sx={{ fontSize: 14, color: "#6A6A6A" }}>
                      Total Time Spent
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      {ticketTimeline.length === 0
                        ? "--/-- min"
                        : `${totalTimeTaken(ticketTimeline)}/${totalTimeBudget(
                            ticketTimeline
                          )} min`}
                    </Typography>
                  </Box>

                  {/* Vetical Separator Line */}
                  <Box
                    sx={{
                      width: "1px",
                      height: "100%",
                      bgcolor: "#D9D9D9",
                      justifySelf: "center",
                    }}
                  />

                  {/* Created Since */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Typography sx={{ fontSize: 14, color: "#6A6A6A" }}>
                      Status Changes
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      {ticketTimeline.length}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
export default TicketActivityTimelineCard;
